import React from 'react';
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

function ScoreGauge({ score }) {
  const color = score >= 70 ? '#22c55e' : score >= 40 ? '#f59e0b' : '#ef4444';
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference * (1 - score / 100);

  return (
    <div className="relative w-32 h-32 mx-auto">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle cx="50" cy="50" r="45" fill="none" stroke={color} strokeWidth="8" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-bold" style={{ color }}>{Math.round(score)}</div>
          <div className="text-xs text-stone-400">/100</div>
        </div>
      </div>
    </div>
  );
}

export function RepresentativenessDashboard({ data, benchmarks, selectedBenchmark, onBenchmarkChange }) {
  const { t } = useTranslation();

  if (!data) return null;

  const radarData = Object.entries(data.dimension_scores || {}).map(([dim, info]) => ({
    dimension: dim.replace(/_/g, ' '),
    score: info.score,
    fullMark: 100,
  }));

  // Pick first dimension for bar chart detail
  const dimensions = Object.entries(data.dimension_scores || {});
  const barData = dimensions.length > 0 ? Object.entries(dimensions[0][1].benchmark_distribution || {}).map(([cat, benchVal]) => ({
    category: cat,
    benchmark: Math.round(benchVal * 100),
    panel: Math.round((dimensions[0][1].panel_distribution?.[cat] || 0) * 100),
  })) : [];

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl">{t('personaLib.representativeness')}</h3>
        <select value={selectedBenchmark} onChange={e => onBenchmarkChange(e.target.value)} className="input text-sm">
          {(Array.isArray(benchmarks) ? benchmarks : []).map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      {/* Overall Score */}
      <ScoreGauge score={data.overall_score || 0} />

      {/* Radar Chart */}
      {radarData.length > 2 && (
        <div className="h-[250px]">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11 }} />
              <Radar dataKey="score" stroke="#d97706" fill="#fef3c7" fillOpacity={0.6} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Dimension scores list */}
      <div className="space-y-2">
        {Object.entries(data.dimension_scores || {}).map(([dim, info]) => (
          <div key={dim} className="flex items-center gap-3">
            <span className="text-sm text-stone-600 w-28 capitalize">{dim.replace(/_/g, ' ')}</span>
            <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${info.score}%`,
                  backgroundColor: info.score >= 70 ? '#22c55e' : info.score >= 40 ? '#f59e0b' : '#ef4444',
                }}
              />
            </div>
            <span className="text-sm font-medium w-10 text-right">{Math.round(info.score)}</span>
          </div>
        ))}
      </div>

      {/* Gap Alerts */}
      {data.gaps && data.gaps.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-stone-700">{t('personaLib.gaps')}</h4>
          {data.gaps.map((gap, idx) => (
            <div key={idx} className={`flex items-start gap-2 p-2 rounded-lg text-sm ${gap.severity === 'high' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{t('personaLib.gapMissing', { dimension: gap.dimension.replace(/_/g, ' '), category: gap.category })} ({Math.round(gap.benchmark_weight * 100)}%)</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
